import { useState, useEffect, useCallback } from 'react';
import { apiService } from 'utils/api';
import debounce from 'lodash/debounce';
import { getUserName } from 'utils/functions';

type QueryOrderUserResType = {
  data: {
    id: string;
    first_name: string;
    last_name: string;
    order_code: string;
    role: string;
  }[];
};

export type TaskHistoryFilterResult = {
  name: string;
  id: string;
  isRep?: boolean;
};

type Props = {
  searchVal: string;
  filterType: string;
};

export const useTaskHistoryPage = ({ searchVal, filterType }: Props) => {
  const [orderUserDatas, setOrderUserDatas] = useState<TaskHistoryFilterResult[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const fetchOrderUsers = useCallback(
    debounce(async (searchVal: string, filterType: string) => {
      if (!searchVal || !filterType) {
        setOrderUserDatas([]);
        return;
      }
      setLoadingSearch(true);
      try {
        const res: { data: QueryOrderUserResType } = await apiService.get(`cs/search/keyword`, {
          baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY,
          params: {
            filter_type: filterType,
            filter_value: searchVal
          }
        });
        if (res.data.data) {
          const users = res.data.data.map((user) => ({
            name: getUserName(user, user.order_code),
            id: user.id,
            isRep: !!user?.role && user.role.toUpperCase() !== 'CUSTOMER'
          }));

          setOrderUserDatas(users);
        }
      } finally {
        setLoadingSearch(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchOrderUsers(searchVal, filterType);
  }, [searchVal, filterType, fetchOrderUsers]);

  useEffect(() => {
    return () => {
      fetchOrderUsers.cancel();
    };
  }, [fetchOrderUsers]);

  return {
    orderUserDatas,
    loadingSearch,
    refetchSearch: () => fetchOrderUsers(searchVal, filterType)
  };
};
